import { useState } from 'react';
import { KeyRound, Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/ui/design-system/components/ui/button';
import { useApiKeys } from 'utils/hooks/apiKeys';
import { useHasPermission } from 'utils/hooks';
import Loading from 'components/Loading';
import CreateKeyDialog from './CreateKeyDialog';
import RevealKeyDialog from './RevealKeyDialog';
import EditKeyDialog from './EditKeyDialog';
import RevokeKeyDialog from './RevokeKeyDialog';

function formatDate(isoString) {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function maskKey(secretStart, secretEnd) {
    if (!secretStart || !secretEnd) return '••••••••••••••••••••••••••••';
    return `${secretStart}${'•'.repeat(32)}${secretEnd}`;
}

function ApiKeys({ createOpen, setCreateOpen }) {
    const { hasPermission: canManageSecretKeys, isReady } = useHasPermission(
        'api.secret_key.manage',
    );
    const {
        data: keys,
        isLoading,
        isError,
    } = useApiKeys({
        enabled: canManageSecretKeys,
    });

    const [revealKey, setRevealKey] = useState(null);
    const [editKey, setEditKey] = useState(null);
    const [revokeKey, setRevokeKey] = useState(null);

    if (!isReady) {
        return (
            <div className="flex justify-center py-12">
                <Loading />
            </div>
        );
    }

    if (!canManageSecretKeys) {
        return (
            <div className="rounded-lg border border-gray-200 bg-white px-6 py-16 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                    <Lock className="h-6 w-6 text-gray-400" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">
                    Access restricted
                </h2>
                <p className="mx-auto mt-1 max-w-md text-pretty text-sm text-gray-500">
                    You don't have permission to manage API keys. Contact your
                    account admin or Spate representative for help.
                </p>
            </div>
        );
    }

    return (
        <div>
            {isLoading ? (
                <div className="flex justify-center py-12">
                    <Loading />
                </div>
            ) : isError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center text-sm text-red-600">
                    Failed to load API keys. Please try again.
                </div>
            ) : keys?.length === 0 ? (
                <div className="rounded-lg border border-gray-200 bg-white px-6 py-16 text-center">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                        <KeyRound className="h-6 w-6 text-gray-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900">
                        No API keys yet
                    </h2>
                    <p className="mx-auto mt-1 max-w-sm text-pretty text-sm text-gray-500">
                        Create your first API key to get started.
                    </p>
                    <Button
                        className="mt-4"
                        onClick={() => setCreateOpen(true)}
                    >
                        <Plus className="h-4 w-4" />
                        Create new key
                    </Button>
                </div>
            ) : (
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50">
                                <th className="px-4 py-3 font-medium text-gray-700">
                                    Name
                                </th>
                                <th className="px-4 py-3 font-medium text-gray-700">
                                    Key
                                </th>
                                <th className="px-4 py-3 font-medium text-gray-700">
                                    Created At
                                </th>
                                <th className="px-4 py-3 text-right font-medium text-gray-700">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {keys.map((key) => (
                                <tr
                                    key={key.id}
                                    className="border-b border-gray-100 last:border-0"
                                >
                                    <td className="px-4 py-3 font-medium text-gray-900">
                                        {key.name}
                                    </td>
                                    <td className="px-4 py-3 font-mono text-gray-500">
                                        {maskKey(
                                            key.secret_start,
                                            key.secret_end,
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-gray-500">
                                        {formatDate(key.created_at)}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => setEditKey(key)}
                                                title="Edit name"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="text-red-500 hover:text-red-700"
                                                onClick={() =>
                                                    setRevokeKey(key)
                                                }
                                                title="Revoke key"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <CreateKeyDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                onKeyCreated={(key) => setRevealKey(key)}
            />
            <RevealKeyDialog
                open={!!revealKey}
                onOpenChange={(open) => {
                    if (!open) setRevealKey(null);
                }}
                apiKey={revealKey || ''}
            />
            <EditKeyDialog
                open={!!editKey}
                onOpenChange={(open) => {
                    if (!open) setEditKey(null);
                }}
                apiKey={editKey}
            />
            <RevokeKeyDialog
                open={!!revokeKey}
                onOpenChange={(open) => {
                    if (!open) setRevokeKey(null);
                }}
                apiKey={revokeKey}
            />
        </div>
    );
}

export default ApiKeys;
